import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api.types';
import { useErrorStore } from '@/stores/errorStore';
// ADR-028 H3 — interceptor reads frontend behavior from the codegen'd
// catalog instead of switch/case on individual error codes. New codes
// added in backend ErrorCatalog automatically pick up the right
// dispatch (e.g. skipGlobalSnackbar for FILE_CORRUPTED-class errors)
// after `make gen` runs.
import { metadataFor } from '@/types/error-codes.generated';

/**
 * What a rejected request carries to the caller: an Error whose message is
 * the catalogued text, plus the HTTP status, the backend error code, any
 * `details` object the backend attached (e.g. USER_HAS_RESOURCES counts) and,
 * for VALIDATION_ERROR, the per-field `fields` map (QA #69) so a page can do
 * `(err as ApiClientError).fields?.email` and mark that input (QA #68).
 */
export type ApiClientError = Error & {
  status?: number;
  code?: string;
  details?: unknown;
  fields?: Record<string, string>;
  originalError?: unknown;
};

// Extend AxiosRequestConfig to include custom options
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipGlobalError?: boolean;
    /** ADR-031 PR-C: marks a request already replayed once after a silent
     *  token refresh, so a second 401 falls through to logout. */
    _authRetry?: boolean;
  }
}

// Guard against multiple 401 redirects
let isRedirectingToLogin = false;

// Get API endpoint from environment variable
// 在 K8s 環境中，VITE_API_ENDPOINT 為空，API 請求會是相對路徑 (e.g. /api/v1/...)
// nginx 會將 /api/* 請求 proxy 到 backend
// 在本地開發時，可設為 http://localhost:8080
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT ?? '';

/**
 * Create axios instance with default configuration
 */
const api: AxiosInstance = axios.create({
  baseURL: API_ENDPOINT,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// ADR-031 PR-C: silent token renewal.
//
// POST /api/v1/auth/refresh exchanges a signature-valid (possibly ≤7d expired)
// JWT for a fresh 24h token. Called reactively by the 401 interceptor below
// (single-flight) and proactively by long uploads via startTokenKeepalive().
// Uses bare axios — the api instance would recurse into its own interceptors.
// ---------------------------------------------------------------------------

let refreshPromise: Promise<string | null> | null = null;

// lastRefreshFailureCode remembers why the server refused the last refresh
// (SESSION_REVOKED / SESSION_IDLE_TIMEOUT / SESSION_EXPIRED / TOKEN_REVOKED)
// so the logout path can tell the user, not just "登入已逾時".
let lastRefreshFailureCode: string | null = null;

// ADR-046: 401 codes that say the session itself is gone — logged out or
// superseded, idle past the window (now enforced on every request, not only
// on refresh), past the 24h cap, or the epoch bumped. The server revoked the
// refresh token along with it, so a refresh cannot bring it back: go straight
// to logout with the code's own message instead of a doomed round-trip.
const TERMINAL_SESSION_CODES = new Set(['SESSION_REVOKED', 'SESSION_IDLE_TIMEOUT', 'SESSION_EXPIRED', 'TOKEN_REVOKED']);

/** Shown when the session is gone but the server gave no specific reason. */
const SESSION_EXPIRED_MESSAGE = '登入已逾時，請重新登入';

// Endpoints whose 401 is a form answer (wrong password, wrong/expired OTP),
// not a dead session (QA #16/#33).
function isAuthFormEndpoint(url: string): boolean {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/change-password') ||
    url.includes('/auth/verify-mfa') ||
    url.includes('/auth/resend-mfa')
  );
}

/**
 * QA #102: why the session ended, for a 401 that survived the silent refresh.
 * The middleware answers an expired access token with the generic
 * UNAUTHORIZED, whose catalogued text ("unauthorized access to file") is
 * meant for a 403 on someone else's file; the real reason is the session code
 * on the 401 itself or the one the refresh endpoint refused with. Falls back
 * to SESSION_EXPIRED_MESSAGE, never to the UNAUTHORIZED text.
 */
function sessionEndReason(responseCode: string | undefined): { code: string | undefined; message: string } {
  const code = TERMINAL_SESSION_CODES.has(responseCode ?? '')
    ? responseCode
    : lastRefreshFailureCode && TERMINAL_SESSION_CODES.has(lastRefreshFailureCode)
      ? lastRefreshFailureCode
      : undefined;
  return {
    code: code ?? responseCode,
    message: (code && metadataFor(code)?.defaultMessage) || SESSION_EXPIRED_MESSAGE,
  };
}

/**
 * QA #102: true when a rejected api call means the login session is gone
 * (401 after the silent refresh, or a terminal session code), so the caller
 * should stop retrying and ask the user to log in again. Only meaningful for
 * calls outside the login/MFA forms.
 */
export function isSessionExpiredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as ApiClientError;
  return e.status === 401 || TERMINAL_SESSION_CODES.has(e.code ?? '');
}

async function requestTokenRefresh(): Promise<string | null> {
  // ADR-046: the refresh token, not the (expired) access token, renews the
  // session; the server rotates it on every call and enforces the 30-minute
  // idle window and the 24-hour cap.
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    const resp = await axios.post<ApiResponse<{ token: string; refresh_token?: string; user?: unknown; expires_at?: string }>>(
      `${API_ENDPOINT}/api/v1/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: 15000 },
    );
    const data = resp.data?.data;
    if (!data?.token) return null;
    localStorage.setItem('token', data.token);
    if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    lastRefreshFailureCode = null;
    console.log('[API] Token refreshed silently');
    return data.token;
  } catch (err) {
    const code = (err as AxiosError<ApiResponse>)?.response?.data?.error?.code;
    lastRefreshFailureCode = typeof code === 'string' ? code : null;
    console.warn('[API] Token refresh failed:', lastRefreshFailureCode ?? err);
    return null;
  }
}

/**
 * Refresh the JWT, deduplicating concurrent callers (single-flight): many
 * requests hitting 401 at once trigger exactly one refresh round-trip.
 * Resolves to the new token, or null when refresh is not possible.
 */
export function refreshAuthToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = requestTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * ADR-054: why the last refresh failed, when the server said so — a terminal
 * session code (SESSION_REVOKED / SESSION_IDLE_TIMEOUT / SESSION_EXPIRED /
 * TOKEN_REVOKED) means the login is over; null after a refresh that never got
 * an answer (network down), which a paused upload keeps waiting out.
 */
export function lastRefreshTerminalCode(): string | null {
  return lastRefreshFailureCode && TERMINAL_SESSION_CODES.has(lastRefreshFailureCode) ? lastRefreshFailureCode : null;
}

/**
 * Proactively refresh the token on an interval while a long-running task
 * (e.g. a multi-hour upload) is in flight, so the 15-minute access token
 * (ADR-046) never lapses mid-transfer. An upload counts as activity, so
 * this does not defeat the idle timeout; the app-wide 20h keepalive that
 * did is gone. Returns a stop function. Default 12m < 15m expiry.
 */
export function startTokenKeepalive(intervalMs: number = 12 * 60 * 1000): () => void {
  const id = window.setInterval(() => {
    void refreshAuthToken();
  }, intervalMs);
  return () => window.clearInterval(id);
}

/**
 * Request Interceptor
 *
 * Automatically adds Authorization header with JWT token to all requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('token');

    // Add Authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData requests, remove Content-Type header to let axios set it automatically
    // with the correct multipart/form-data boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Log request in development mode
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data instanceof FormData ? '[FormData]' : config.data,
      });
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 *
 * Handles errors globally and displays appropriate messages
 * - 401 Unauthorized: Clear auth and redirect to login
 * - 403 Forbidden: Show error message
 * - 500 Server Error: Show error message
 * - Network Error: Show network error message
 */
api.interceptors.response.use(
  (response) => {
    // Log response in development mode
    if (import.meta.env.DEV) {
      console.log(`[API] Response from ${response.config.url}:`, response.data);
    }
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    const { showError } = useErrorStore.getState();
    const status = error.response?.status;
    // Blob response (responseType: 'blob') carries error JSON inside a Blob;
    // axios does not auto-parse it, so errorData ends up undefined and the
    // page-level handler can never read the code. Read it manually.
    if (error.response?.data && typeof Blob !== 'undefined' && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        if (text) {
          error.response.data = JSON.parse(text);
        }
      } catch {
        // Non-JSON blob — leave data as-is and let downstream handlers show a
        // generic message.
      }
    }
    const errorData = error.response?.data?.error;
    const requestUrl = error.config?.url || '';
    const skipGlobalError = error.config?.skipGlobalError === true;

    // ADR-031 PR-C: on 401, first try a silent refresh and replay the
    // original request once. This runs BEFORE the skipGlobalError early
    // return so long uploads (which set skipGlobalError) also renew.
    // Auth endpoints themselves are excluded; a replayed request that 401s
    // again falls through to the logout handling below.
    if (
      status === 401 &&
      error.config &&
      !error.config._authRetry &&
      !TERMINAL_SESSION_CODES.has(errorData?.code ?? '') &&
      !requestUrl.includes('/auth/refresh') &&
      // QA #16/#33: login/register, change-password and the MFA steps carry
      // their own 4xx form errors (wrong current password, wrong/expired
      // OTP). Refreshing then replaying them, and finally logging the user
      // out on the second 401, threw away the change-password error and the
      // MFA step. Excluded so a 401 here (e.g. an expired mfa_token) is
      // surfaced to the page, not turned into a logout.
      !isAuthFormEndpoint(requestUrl)
    ) {
      const newToken = await refreshAuthToken();
      if (newToken) {
        const retryConfig = error.config;
        retryConfig._authRetry = true;
        if (retryConfig.headers) {
          retryConfig.headers.Authorization = `Bearer ${newToken}`;
        }
        console.log('[API] Replaying request after silent token refresh:', requestUrl);
        return api.request(retryConfig);
      }
      // Refresh failed — fall through to the existing 401 handling (logout).
    }

    // Message shown to the user. Prefer the code's catalogued Chinese message
    // (codegen from the backend ErrorCatalog); never surface the backend's raw
    // `message`, which is often English or a technical string (QA #4). The raw
    // message is still logged and handed to pages via skipGlobalError.
    //
    // QA #102: a 401 that got here (refresh failed or not allowed) means the
    // session is over; say so instead of the code's catalogued text, which
    // for UNAUTHORIZED is the 403 file-ownership line.
    const sessionEnd =
      status === 401 && !isAuthFormEndpoint(requestUrl) ? sessionEndReason(errorData?.code) : null;
    const errorCode = sessionEnd ? sessionEnd.code : errorData?.code;
    const catalogued = sessionEnd ? sessionEnd.message : metadataFor(errorData?.code)?.defaultMessage;
    const rawMessage = errorData?.message || error.message || '';
    const friendlyMessage = catalogued || '操作失敗，請稍後再試';

    // Skip global error display if requested (for chunked upload, etc.). The
    // page gets the code and the raw message so it can decide what to show.
    if (skipGlobalError) {
      console.error('[API] Error (skipGlobalError):', rawMessage || friendlyMessage);
      const apiError = new Error(catalogued || rawMessage || friendlyMessage) as ApiClientError;
      apiError.status = status;
      apiError.code = errorCode;
      apiError.details = errorData?.details;
      apiError.fields = errorData?.fields;
      apiError.originalError = error;
      return Promise.reject(apiError);
    }

    // Handle different error status codes
    switch (status) {
      case 401:
        // Check if this is a login or register request - don't show global error or redirect
        // The MFA and change-password endpoints handle their own errors too
        // (QA #16/#33): a 401 there means the form input or the mfa_token was
        // wrong, not that the session is dead, so the page shows it.
        if (isAuthFormEndpoint(requestUrl)) {
          // Let the page component display the error
          console.error('[API] 401 on auth endpoint:', rawMessage);
          break;
        }

        // Unauthorized - Clear auth data and redirect to login. Say why
        // (ADR-046): superseded by another device, idle, 24h cap, revoked.
        console.error('[API] 401 Unauthorized - Redirecting to login');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');

        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true;
          // QA #102: the session code (on the 401 or from the refused
          // refresh), never UNAUTHORIZED's file-permission text.
          showError(sessionEnd?.message || SESSION_EXPIRED_MESSAGE);
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
        }
        break;

      case 400:
        // Bad request — a form/validation error. Show the catalogued message
        // when there is one, otherwise a generic line (QA #4: no raw backend
        // strings). Before this, 400 fell through to `default` and popped the
        // raw message.
        console.error('[API] 400 Bad Request:', rawMessage);
        showError(catalogued || '輸入內容有誤，請確認後再試');
        break;

      case 403:
        // Forbidden - No permission
        console.error('[API] 403 Forbidden:', rawMessage);
        showError(catalogued || '您沒有權限執行此操作');
        break;

      case 404:
        // Not Found
        console.error('[API] 404 Not Found:', rawMessage);
        showError(catalogued || '找不到請求的資源');
        break;

      case 422:
        // ADR-028 H3 — map-driven dispatch via ERROR_METADATA (codegen
        // from backend ErrorCatalog). Adding a new code with
        // skipGlobalSnackbar behavior in backend takes effect here
        // automatically; no per-code branch needed.
        // Hardcoded check kept as a defensive backstop in case a new
        // code arrives before the codegen runs.
        {
          const meta = metadataFor(errorData?.code);
          if (
            meta?.frontendBehavior === 'skipGlobalSnackbar' ||
            errorData?.code === 'FILE_CORRUPTED'
          ) {
            console.warn(
              `[API] 422 ${errorData?.code ?? '<no-code>'} - skipping global snackbar (page will handle)`,
            );
            break;
          }
        }
        console.error('[API] 422 Validation Error:', rawMessage);
        showError(catalogued || '資料驗證失敗');
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server Error
        console.error(`[API] ${status} Server Error:`, rawMessage);
        showError('伺服器錯誤，請稍後再試');
        break;

      default:
        // Network error or other errors
        if (!error.response) {
          console.error('[API] Network Error:', error.message);
          showError('網路連線失敗，請檢查網路狀態');
        } else {
          console.error(`[API] Error ${status}:`, rawMessage);
          showError(friendlyMessage);
        }
    }

    // Log full error in development mode
    if (import.meta.env.DEV) {
      console.error('[API] Full error:', error);
    }

    // Return rejected promise with formatted error
    // Create an Error instance so it can be caught with 'instanceof Error'
    const apiError = new Error(catalogued || rawMessage || friendlyMessage) as ApiClientError;
    apiError.status = status;
    apiError.code = errorCode;
    apiError.details = errorData?.details;
    apiError.fields = errorData?.fields;
    apiError.originalError = error;

    return Promise.reject(apiError);
  }
);

export default api;
